import { ComponentType } from "react";
import RobotOutlineIcon from "@src/lib/icons/18px/robot-outline";
import LinkIcon from "@src/lib/icons/18px/link";
import VideoOutlineIcon from "@src/lib/icons/18px/video-outline";
import MapOutlineIcon from "@src/lib/icons/18px/map-outline";
import GridLayoutCols3Icon from "@src/lib/icons/18px/grid-layout-cols-3";
import UserOutlineIcon from "@src/lib/icons/18px/user-outline";
import ClipboardCheckIcon from "@src/lib/icons/18px/clipboard-check";
import NewspaperOutlineIcon from "@src/lib/icons/18px/newspaper-outline";
import LaptopOutlineIcon from "@src/lib/icons/18px/laptop-outline";
import ClockOutlineIcon from "@src/lib/icons/18px/clock-outline";
import EyeOutlineIcon from "@src/lib/icons/18px/eye-outline";
import LanguageIcon from "@src/lib/icons/18px/language";
import MicrophoneOutlineIcon from "@src/lib/icons/18px/microphone-outline";
import { IconProps } from "@src/lib/icons/types";
import { Ff, UserType } from "@src/types.generated";

// Monthly releases surfaced at the top of the Admin and Manager homepages.
// Release slugs, copy, and assets mirror the release pages in the `changelog`
// repo (src/lib/releases.ts) so links resolve and the two read alike.

export const RELEASES_BASE_URL = "https://changelog.opus.so/releases";
// The changelog moved into the marketing site; changelog.opus.so redirects
// there, but only release pages survived the move, not their media paths.
export const RELEASE_PAGES_URL = "https://www.opus.so/changelog/releases";
export const HELP_CENTER_URL = "https://help.opus.so/en/";

export type ReleaseAudience = UserType.Admin | UserType.Manager;
// Copy per role: every release speaks to Admins; Manager copy only where the
// release's audience includes Managers.
type PerRole = Record<UserType.Admin, string> &
  Partial<Record<UserType.Manager, string>>;

// How much room a feature gets, mirroring the changelog release page:
// - "lead": the headline feature, selected when the letter opens.
// - "companion": shown alongside the lead (e.g. Opus MCP next to Agents).
// - "more": listed by name under "Also new" on the homepage card.
export type ReleaseFeatureTier = "lead" | "companion" | "more";

export type ReleaseHeadlineFeature = {
  slug: string;
  title: string;
  // One short line about the feature, from the changelog release page.
  deck: string;
  icon: ComponentType<IconProps>;
  tier: ReleaseFeatureTier;
  // The feature's clip from the changelog release page, shown in the letter
  // when the feature is selected: asset URL without extension (it serves
  // `.mp4` and a `.jpg` poster), with the video's size so the layout doesn't
  // jump as it loads.
  video?: { url: string; width: number; height: number };
  // Logos of the tools a companion works with.
  logos?: { name: string; src: string }[];
  // Who sees the feature; defaults to Admins and Managers.
  audience?: ReleaseAudience[];
};

// A lead story in the left nav letter.
export type ReleaseLetterStory = {
  // The story's section on the release page, which its link opens.
  slug: string;
  // The blue label over the story's title, if any.
  eyebrow?: string;
  title: string;
  deck: string;
  icon: ComponentType<IconProps>;
  // The story's one link: `label` opens its section of the release page;
  // "newsfeed" depends on whether the org is on Newsfeed yet. None before
  // the release page is published.
  link?: { label: string } | "newsfeed";
  // Who reads the story; defaults to Admins and Managers.
  audience?: ReleaseAudience[];
};

// One "Also new" bullet in the left nav letter: "**Name.** Line."
export type ReleaseLetterItem = {
  slug: string;
  name: string;
  line: string;
  audience?: ReleaseAudience[];
};

// The left nav letter's own copy.
export type ReleaseLetterCopy = {
  // The nav card's title, e.g. "November Release".
  title: string;
  // The line under the title on the unread nav card, per role.
  teaser: PerRole;
  // The letter's heading, per role.
  headline: PerRole;
  stories: ReleaseLetterStory[];
  alsoNew: ReleaseLetterItem[];
  // The heading over `alsoNew`; defaults to "Also new".
  alsoNewTitle?: string;
  // Before launch: a footer button lets Admins who can change org settings
  // opt their company in early, turning on `flags` (scadmin's
  // optIntoNovemberRelease does the same), and their nav card's teaser says
  // so. `excludeSlugs` are features the opt-in leaves alone.
  optIn?: {
    label: string;
    teaser: string;
    flags: Ff[];
    excludeSlugs: string[];
  };
  // The handwritten sign-off, one line each.
  signOff: string[];
};

// How the release is introduced on the homepage.
// - "card": an inline headline card at the top of the homepage.
// - "envelope": a sealed envelope pops up the first time; opening it reveals
//   the announcement as a letter, and later visits show the inline card.
// - "nav-letter": an envelope card waits at the bottom of the left nav until
//   it's opened; opening it shows the release as a letter. The homepage
//   shows nothing for it.
export type ReleaseExperience = "card" | "envelope" | "nav-letter";

export type ReleaseHeadlineRelease = {
  slug: string;
  // The release page's slug, when it isn't `slug` (an announcement ahead of
  // the release shares its page).
  pageSlug?: string;
  month: string;
  headline: string;
  // One line under the headline, per role.
  subline: PerRole;
  // Local-time window during which the headline shows on the homepage.
  startsAt: string;
  endsAt: string;
  experience: ReleaseExperience;
  // Who sees the release; defaults to Admins only.
  audience?: ReleaseAudience[];
  // When set, only viewers with this flag on see the release (previews
  // ignore it), so it can roll out business by business.
  flag?: Ff;
  // The "nav-letter" experience's copy. Its "lead" and "companion" features
  // are the letter's stories; "more" features are listed under "Also new".
  letter?: ReleaseLetterCopy;
  features: ReleaseHeadlineFeature[];
};

// Newest first. The first release whose window contains today is shown.
// November's features go live on November 11. Until then the letter
// announces them and lets Admins opt in early; on launch day the release
// takes over as a fresh, unread letter.
const LAUNCH_AT = "2026-11-11T00:00:00";

const NOVEMBER: ReleaseHeadlineRelease = {
  slug: "2026-11",
  month: "November",
  headline: "The November 2026 release is here",
  subline: {
    [UserType.Admin]: "Give your Managers their shift back",
  },
  startsAt: LAUNCH_AT,
  endsAt: "2026-12-01T00:00:00",
  experience: "nav-letter",
  // Admins only (Ben, 2026-11-04): Managers see no card, letter or banner.
  letter: {
    title: "November Release",
    teaser: {
      [UserType.Admin]: "Give your managers their shift back",
    },
    headline: {
      [UserType.Admin]: "Give your managers their shift back",
    },
    // Copy from the release page, cut to Ben's letter: one deck and one
    // link per story, and a sentence per "Also new" bullet.
    stories: [
      {
        slug: "agents-for-managers",
        eyebrow: "Agents for Managers",
        title: "Put AI to work on the floor",
        deck: "Every Manager gets a daily summary of what’s due, a snapshot of their location health, and a new hire coach.",
        icon: RobotOutlineIcon,
        link: { label: "Meet your new AI teammates" },
        audience: [UserType.Admin],
      },
      {
        slug: "newsfeed-and-messages",
        eyebrow: "Communication Hub",
        title: "All your team communication in the app where work happens",
        deck: "Build culture across every location and coordinate every shift in real time. You have full control on when the new experience goes live for your team.",
        icon: NewspaperOutlineIcon,
        link: "newsfeed",
        // Not for Managers yet: an Admin turns it on first.
        audience: [UserType.Admin],
      },
    ],
    alsoNew: [
      {
        slug: "voice-mode",
        name: "Voice Mode",
        line: "Talk through a Checklist, Audit, or Check-in, and Opus fills in the steps.",
      },
      {
        slug: "checklists-on-desktop",
        name: "Checklists on desktop",
        line: "Fill out Checklists, Audits, and Check-ins on a computer.",
      },
      {
        slug: "late-submissions",
        name: "Late submissions",
        line: "Give any scheduled Checklist a late window.",
      },
      {
        slug: "impersonation",
        name: "View as",
        line: "See exactly what your team sees.",
        audience: [UserType.Admin],
      },
      {
        slug: "better-translations",
        name: "Better translations",
        line: "Translation quality is even better.",
      },
    ],
    signOff: ["– Team Opus"],
  },
  // Copy follows the release page (marketing-site src/data/releases.ts):
  // its names and headlines (as decks). Slugs are the page's section
  // anchors.
  features: [
    {
      slug: "agents-for-managers",
      title: "Agents for Managers",
      deck: "Put AI to work on the floor",
      icon: RobotOutlineIcon,
      tier: "lead",
    },
    {
      slug: "newsfeed-and-messages",
      title: "Newsfeed and Messages",
      deck: "All your team communication in the app where work happens",
      icon: NewspaperOutlineIcon,
      tier: "companion",
      // Not for Managers yet: an Admin turns it on first.
      audience: [UserType.Admin],
    },
    {
      slug: "voice-mode",
      title: "Voice Mode",
      deck: "Talk through a Checklist, Audit, or Check-in",
      icon: MicrophoneOutlineIcon,
      tier: "more",
    },
    {
      slug: "checklists-on-desktop",
      title: "Checklists on desktop",
      deck: "More ways to get work done",
      icon: LaptopOutlineIcon,
      tier: "more",
    },
    {
      slug: "late-submissions",
      title: "Late submissions",
      deck: "Track late Checklists",
      icon: ClockOutlineIcon,
      tier: "more",
    },
    {
      // The page keeps the feature's old anchor.
      slug: "impersonation",
      title: "View as",
      deck: "See exactly what your team sees",
      icon: EyeOutlineIcon,
      tier: "more",
      audience: [UserType.Admin],
    },
    {
      slug: "better-translations",
      title: "Better translations",
      deck: "Translation quality is even better",
      icon: LanguageIcon,
      tier: "more",
    },
  ],
};

const NOVEMBER_ANNOUNCEMENT: ReleaseHeadlineRelease = {
  ...NOVEMBER,
  slug: "2026-11-announcement",
  pageSlug: NOVEMBER.slug,
  headline: "The November 2026 release arrives November 11",
  startsAt: "2026-11-04T00:00:00",
  endsAt: LAUNCH_AT,
  letter: {
    ...NOVEMBER.letter!,
    teaser: {
      [UserType.Admin]: "Live November 11",
    },
    headline: {
      [UserType.Admin]:
        "On November 11, help your managers get their shift back",
    },
    // One call to action, the opt-in button, instead of a link per story.
    stories: NOVEMBER.letter!.stories.map((story) => ({
      ...story,
      link: undefined,
    })),
    alsoNewTitle: "Also coming",
    optIn: {
      label: "Get early access",
      teaser: "Live November 11, or get early access",
      // Everything but Newsfeed and Messages, which keeps its own setup.
      // Better translations also switches on, but only for orgs with a
      // glossary, so it doesn't count toward "already opted in".
      flags: [
        Ff.OpusAgentManagers,
        Ff.SkillFormVoice,
        Ff.WebFormCompletion,
        Ff.LateChecklistSubmissions,
        Ff.Impersonation,
      ],
      excludeSlugs: ["newsfeed-and-messages"],
    },
  },
};

export const RELEASES: ReleaseHeadlineRelease[] = [
  NOVEMBER,
  NOVEMBER_ANNOUNCEMENT,
  {
    slug: "2026-10",
    month: "October",
    headline: "Put Opus to work with agents",
    subline: {
      [UserType.Admin]:
        "Automate the tedious work so that you can focus on what’s high impact.",
      [UserType.Manager]:
        "See how your training is doing on one page, grade Audits your way, and ask Claude or ChatGPT about your team’s training.",
    },
    startsAt: "2026-10-01T00:00:00",
    endsAt: "2026-11-01T00:00:00",
    experience: "envelope",
    flag: Ff.OctoberReleaseEnvelope,
    features: [
      {
        slug: "opus-agents",
        title: "Opus Agents",
        deck: "Build agents that know your team and get work done for you.",
        video: {
          url: `${RELEASES_BASE_URL}/2026-10/agents`,
          width: 1100,
          height: 688,
        },
        icon: RobotOutlineIcon,
        tier: "lead",
        audience: [UserType.Admin],
      },
      {
        slug: "opus-mcp",
        title: "Opus MCP",
        deck: "Integrate with your AI tool",
        logos: [
          {
            name: "Claude",
            src: `${RELEASES_BASE_URL}/2026-10/ai-tools/claude.svg`,
          },
          {
            name: "ChatGPT",
            src: `${RELEASES_BASE_URL}/2026-10/ai-tools/openai.svg`,
          },
          {
            name: "Gemini",
            src: `${RELEASES_BASE_URL}/2026-10/ai-tools/gemini.svg`,
          },
          {
            name: "Copilot",
            src: `${RELEASES_BASE_URL}/2026-10/ai-tools/copilot.svg`,
          },
        ],
        icon: LinkIcon,
        tier: "companion",
      },
      {
        slug: "video-editor",
        video: {
          url: `${RELEASES_BASE_URL}/2026-10/video-editor`,
          width: 1400,
          height: 1080,
        },
        title: "Video Editor",
        deck: "Trim clips, add a voiceover or an AI voice, and add music, then save it in place.",
        icon: VideoOutlineIcon,
        tier: "more",
      },
      {
        slug: "improved-path-builder",
        video: {
          url: `${RELEASES_BASE_URL}/2026-10/path`,
          width: 1400,
          height: 1080,
        },
        title: "Improved Path Builder",
        deck: "Keep changes in draft until you publish, and see who's on each step in the new Runs tab.",
        icon: MapOutlineIcon,
        tier: "more",
      },
      {
        slug: "content-detail-pages",
        video: {
          url: `${RELEASES_BASE_URL}/2026-10/detail`,
          width: 1024,
          height: 768,
        },
        title: "Content Overview",
        deck: "A new Overview shows results, who has it, and a phone preview of what your team sees.",
        icon: GridLayoutCols3Icon,
        tier: "more",
      },
      {
        slug: "profile-photos",
        video: {
          url: `${RELEASES_BASE_URL}/2026-10/profile`,
          width: 1436,
          height: 1080,
        },
        title: "Profile Photos",
        deck: "Let your team personalize their profiles, and recognize each other more easily.",
        icon: UserOutlineIcon,
        tier: "more",
      },
      {
        slug: "audit-grades",
        video: {
          url: `${RELEASES_BASE_URL}/2026-10/audit`,
          width: 1400,
          height: 1080,
        },
        title: "Audit Grades",
        deck: "Grade every Audit and each section from Excellent to Needs improvement, using ranges you set.",
        icon: ClipboardCheckIcon,
        tier: "more",
      },
    ],
  },
];

export const releaseUrl = (release: ReleaseHeadlineRelease): string =>
  `${RELEASE_PAGES_URL}/${release.pageSlug ?? release.slug}`;

// The release page, tagged so the changelog's analytics can tell visits from
// the dashboard apart from everything else.
export const trackedReleaseUrl = (
  release: ReleaseHeadlineRelease,
  medium: ReleaseExperience,
): string =>
  `${releaseUrl(release)}?${new URLSearchParams({
    utm_source: "dashboard",
    utm_medium: medium,
    utm_campaign: release.slug,
  })}`;

export const DEFAULT_AUDIENCE: ReleaseAudience[] = [UserType.Admin];

export const audienceFor = (
  release: ReleaseHeadlineRelease,
): ReleaseAudience[] => release.audience ?? DEFAULT_AUDIENCE;

export const featuresFor = (
  release: ReleaseHeadlineRelease,
  userType: ReleaseAudience,
): ReleaseHeadlineFeature[] =>
  release.features.filter(
    (feature) => !feature.audience || feature.audience.includes(userType),
  );

export const getActiveRelease = (
  now: Date,
  previewSlug?: string,
): ReleaseHeadlineRelease | null => {
  if (previewSlug) {
    return RELEASES.find((release) => release.slug === previewSlug) ?? null;
  }
  return (
    RELEASES.find(
      (release) =>
        now >= new Date(release.startsAt) && now < new Date(release.endsAt),
    ) ?? null
  );
};
